

















































































































import Vue, { PropType } from 'vue'
import { mapGetters } from 'vuex'
import Broadcast from '@/types/Broadcast.interface'
import Group from '@/types/Group.interface'
import GroupSelector from '@/components/GroupSelector.vue'
import Media from '@/types/Media.interface'
import modelsMixin from '@/mixins/models-mixin'
import PersonsAutoComplete from './PersonsAutoComplete.vue'
import { randomKey } from '@/utility/helpers'
import ScheduleBuilder from '@/components/ScheduleBuilder.vue'
import Person from '@/types/Person.interface'
import { debounce } from 'debounce'
export default Vue.extend({
    mixins: [modelsMixin],
    components: { GroupSelector, PersonsAutoComplete, ScheduleBuilder },
    props: {
        //  Determines the current loading state
        loading: {
            type: Boolean,
            default: false
        },
        //  The broadcast being updated or created
        temp: {
            type: Object as PropType<Broadcast>,
            required: true
        },
        tempMessage: {
            type: String,
            default: ''
        },
        //  We pass the consent message text associated with the tenant from BroadcastForm.vue so we don't have to repeat code
        consentMsg: {
            type: String, 
            default: '',
            required: true
        },
        //  We pass the company name text associated with the tenant from BroadcastForm.vue so we don't have to repeat code
        companyName: {
            type: String, 
            default: '',
            required: true
        }
    },
    data: () => ({
        //  Determines if the opt out message is appended or not
        consentToggle: true,
        //  Determines if the organization name is appended or not
        companyToggle: true,
        //  Determines if our exclude search bar is shown
        excludeToggleData: false,
        //  Condiiton to monitor if a group has been selected
        groupSelected: null,
        //  Conditional rendering for messagetextform valdiation
        messageEmpty: null,
        //  The key bound to our text form so we can remount it
        textFormKey: randomKey(),
        //  Shows group selector
        showSendToGroupSelector: true,
        //  Shows exclude group selector
        showExcludeGroupSelector: false
    }),
    //  We have to repeat logic here as this component has to sync with both BroadcastForm.vue (parent) and BroadcastPhonePreview.vue (sibling)
    //  Changes in this function may occur and it has to propogate up to parent and then down to the sibling, going from the parent level to both children 
    //  doesn't work as this form passes changes up to the parent as BroadcastForm.vue isn't interacted with directly by the user
    mounted() {
        //  When creating a broadcast this will be null so we figure out if this is a new broadcast or a scheduled/draft broadcast
        let msg = this.temp.message || ''

        //  We don't manage our opt-in and company name toggles if we're a brand new broadcast, thus these default to being on 
        if (!msg) return
        
        //  Determine if the company name and opt-out verbiage are already in our broadcast message
        const containsCompanyName = msg.includes(this.companyName)
        const containsConsent = msg.includes(this.consentMsg)
        
        //  If we don't have the company name then we know that the user intentionally turned it off, we turn off the toggle and propogate the changes up. 
        if (!containsCompanyName) {
            this.companyToggle = false
            this.$emit('companyToggled', this.companyToggle)
        }

        //  The same reasoning and logic applies here as above 
        if (!containsConsent) {
            this.consentToggle = false
            this.$emit('consentToggled', this.consentToggle)
        }

        //  If the broadcast is a draft or scheduled we need to remove the company name and consent message since when a user re-enters the broadcast form these messages will appear in the 
        //  message compose box. We use the message field as a means of storage to know what state our toggle should be in to workaround the fact that these two toggles
        //  are not in the DB. We directly edit the temp broadcast and the watcher in BroadcastForm.vue updates the broadcast accordingly. Note that this doesn't mean the broadcast when sent
        //  will not have this text in it, it will since in our BroadcastPreview.vue and BroadcastMessageTest.vue we append these values back anyways if the toggle is on.  
        if (this.temp.status === 'draft' || this.temp.status === 'scheduled') {
            msg = msg.replace(this.companyName, '')
            msg = msg.replace(this.consentMsg, '')

            //  This is needed otherwise the broadcast text box in this component will add the opt-out and company name text to it which we don't want 
            this.onMessageChanged(msg)
        }
    },
    computed: {
        ...mapGetters([
            'allContactsGroup',
            'broadcastsViewMediaHint',
            'isApp',
            'optOutGroup'
        ]),
        excludeFromSendGroups(): number[] {
            //  Merge send to groups and excluded groups
            // This is then inserted into child component so that these may be excluded from selection
            const merged = [...this.temp.group_ids, ...this.temp.exclude_group_ids]

            //  Return the merged array
            return merged
        },
        excludeFromExcludeGroups(): number[] | number {
            //  Merge send to groups and excluded groups
            // This is then inserted into child component so that these may be excluded from selection
            const merged = [...this.excludeFromSendGroups, this.allContactsGroup.id]

            //  Return the merged array
            return merged
        },
        //  Our items we use for displaying the exclude chips
        excludeFromItems(): Group[] | Person[] {
            //  Grab all of our groups
            const groups = this.temp.exclude_groups || []

            //  Grab all of our persons
            const persons = this.temp.exclude_persons || []

            //  A container for our persons since 'name' is not an existing property
            const formattedPersons = []

            //  Iterate through each person and create a simple object just for displaying
            persons.forEach((x: Person) =>
                formattedPersons.push({
                    id: x.id,
                    object: 'Person',
                    name: this.$options.filters.personFullNameOrPhone(x)
                })
            )

            //  Combine our arrays
            const merged = [...groups, ...formattedPersons]

            //  Return our merged array of send to items
            return merged
        },
        excludePersons(): number[] {
            //  Merge persons and excluded persons
            //  This is then inserted into child component so that these may be excluded from selection
            const merged = [
                ...this.temp.person_ids,
                ...this.temp.exclude_person_ids,
                ...this.preseededPersons
            ]

            //  Return merged array
            return merged
        },
        excludeToggle: {
            get(): boolean {
                //  If our toggle is NOT enabled then we always return true
                if (this.excludeToggleDisabled) return true

                //  Else, base it off of our data variable
                return this.excludeToggleData
            },
            set(val: boolean): void {
                this.excludeToggleData = val
            }
        },
        excludeToggleDisabled(): boolean {
            //  Grab our exclude groups and persons
            const exclude = [
                ...this.temp.exclude_person_ids,
                ...this.temp.exclude_group_ids
            ]

            //  If the length is greater than 1 (means more than opt out) then return FALSE
            return exclude.length > 1
        },
        preseededPersons(): number[] {
            //  Get preseeded persons
            const persons = this.$store.getters.preseededPersons

            //  If no persons, return nothing
            if (!persons) return []

            //  Map their ids because that's all we need
            const personsIds = persons.map((x: Person) => x.id)

            //  Return those ids
            return personsIds
        },
        //  Our items we use for displaying the send to chips
        sendToItems(): Group[] | Person[] {
            //  Grab all of our groups
            const groups = this.temp.groups || []

            //  Grab all of our persons
            const persons = this.temp.persons || []

            //  A container for our persons since 'name' is not an existing property
            const formattedPersons = []

            //  Iterate through each person and create a simple object just for displaying
            persons.forEach((x: Person) =>
                formattedPersons.push({
                    id: x.id,
                    object: 'Person',
                    name: this.$options.filters.personFullNameOrPhone(x)
                })
            )

            //  Combine our arrays
            const merged = [...groups, ...formattedPersons]

            //  Return our merged array of send to items
            return merged
        }
    },
    methods: {
        companyToggled(bool: boolean): void {
            //  Tell our parent whether or not we want to toggle our company name
            this.$emit('companyToggled', bool)
            
            //  If user is removing org name we pop a dialog explaining why it's in best interest compliance wise to include this.
            if (!bool) this.popOrgNameInfo()
        },
        consentToggled(bool: boolean): void {
            //  Tell our parent whether or not we want to toggle our consent message
            this.$emit('consentToggled', bool)
            
            //  If user is removing opt-out verbiage we pop a dialog explaining why it's in best interest compliance wise to include this.
            if (!bool) this.popOptOutInfo()
        },
        onExcludeFromRemoved(item: Group | Person): void {
            //  Check if this is a group
            const isGroup = item.object === 'Group'

            //  If this is a group then we need to remove it from the group list
            if (isGroup) {
                //  Find this item in our list
                const groupIdx = this.temp.exclude_group_ids.findIndex(
                    (x: number) => x === item.id
                )

                //  Remove it from our id list
                this.temp.exclude_group_ids.splice(groupIdx, 1)

                //  Remove it from the group list too
                this.temp.exclude_groups.splice(groupIdx, 1)
            }
            //  Else, this is a person so do the same but for our persons list
            else {
                //  Find this item in our list
                const personIdx = this.temp.exclude_person_ids.findIndex(
                    (x: number) => x === item.id
                )

                //  Remove it from our id list
                this.temp.exclude_person_ids.splice(personIdx, 1)

                //  Remove it from the person list too
                this.temp.exclude_persons.splice(personIdx, 1)
            }

            //  Get the total number of contacts this broadcast gets sent to
            this.$store.dispatch('broadcastsViewSelectedBroadcastTotalCount', this.temp)
        },
        onExcludeToggled: debounce(function (): void {
            //  Check if we can toggle
            const disabled = this.excludeToggleDisabled

            //  If we cannot toggle then pop a dialog
            if (disabled) this.popExcludeToggleBlocked()
        }, 100),
        onMediaAdded(media: Media | null): void {
            //  Attach the media to our broadcast
            this.temp.media = media

            //  Also update media id
            this.temp.media_id = media ? media.id : null

            //  Emit mediaAdded with media attached
            this.$emit('mediaAdded', media)
        },
        onMessageChanged(text: string): void {
            //  Update the broadcast we're editing
            this.temp.message = text

            //  Emit messageInputted with text attached
            this.$emit('messageInputted', text)
        },
        onSendToRemoved(item: Group | Person): void {
            //  Check if this is a group
            const isGroup = item.object === 'Group'

            //  If this is a group then we need to remove it from the group list
            if (isGroup) {
                //  Find this item in our list
                const groupIdx = this.temp.group_ids.findIndex(
                    (x: number) => x === item.id
                )

                //  Remove it from our id list
                this.temp.group_ids.splice(groupIdx, 1)

                //  Remove it from the group list too
                this.temp.groups.splice(groupIdx, 1)
            }
            //  Else, this is a person so do the same but for our persons list
            else {
                //  Find this item in our list
                const personIdx = this.temp.person_ids.findIndex(
                    (x: number) => x === item.id
                )

                //  Remove it from our id list
                this.temp.person_ids.splice(personIdx, 1)

                //  Remove it from the person list too
                this.temp.persons.splice(personIdx, 1)
            }

            //  Get the total number of contacts this broadcast gets sent to
            this.$store.dispatch('broadcastsViewSelectedBroadcastTotalCount', this.temp)
        },
        popExcludeInfo(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Exclude Contacts and Groups',
                text: 'Exclude specific contacts or groups from your broadcast message. Contacts or groups added to your exclude select will NOT receive the broadcast message. by default, all opted out contacts are excluded.',
                icon: 'mdi-help-circle',
                color: 'primary'
            })
        },
        //  This might require debounce depending on how some users are but we are good for now
        popExcludeSearch() {
            //  Blur our component
            this.$refs.excludeSearch.blur()

            //  Grab the current excluded groups
            const groups = this.temp.exclude_groups

            //  Remove our opt out
            const excludeGroups = groups.filter(
                (x: Group) => x.id !== this.optOutGroup.id
            )

            //  Open our search pop up
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'unified-search',
                closeable: false,
                closeOnSubmit: true,
                overflow: 'hidden',
                props: {
                    exclude: true,
                    excludeGroups: this.excludeFromExcludeGroups,
                    excludePersons: this.excludePersons,
                    groups: excludeGroups,
                    persons: this.temp.exclude_persons
                },
                callback: (response) => {
                    //  Grab our persons from the response
                    const persons = response.persons

                    //  Make sure that our exclude groups contains our opt out AT ALL TIMES
                    const groups = [...response.groups, this.optOutGroup]

                    //  Handle adding these groups and persons to our broadcast
                    this.temp.exclude_groups = groups
                    this.temp.exclude_persons = persons

                    //  Map these to ids
                    const groupIds = groups.map((x: Group) => x.id)
                    const personIds = persons.map((x: Person) => x.id)

                    //  Update our ids
                    this.temp.exclude_group_ids = groupIds
                    this.temp.exclude_person_ids = personIds

                    //  Get the total number of contacts this broadcast gets sent to
                    this.$store.dispatch(
                        'broadcastsViewSelectedBroadcastTotalCount',
                        this.temp
                    )
                }
            })
        },
        popExcludeToggleBlocked(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Toggling Exclude Disabled',
                text: 'Please remove excluded contacts and groups before toggling exclude off',
                icon: 'mdi-alert',
                color: 'primary'
            })
        },
        popMemberSearch() {
            //  Blur our component
            this.$refs.memberSearch.blur()

            //  Open our search pop up
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'unified-search',
                closeable: false,
                closeOnSubmit: true,
                overflow: 'hidden',
                props: {
                    excludeGroups: this.excludeFromSendGroups,
                    excludePersons: this.excludePersons,
                    groups: this.temp.groups,
                    persons: this.temp.persons
                },
                callback: (response) => {
                    //  Grab our groups and persons from the response
                    const groups = response.groups
                    const persons = response.persons

                    //  Handle adding these groups and persons to our broadcast
                    this.temp.groups = groups
                    this.temp.persons = persons

                    //  Map these to ids
                    const groupIds = groups.map((x: Group) => x.id)
                    const personIds = persons.map((x: Person) => x.id)

                    //  Update our ids
                    this.temp.group_ids = groupIds
                    this.temp.person_ids = personIds

                    //  Get the total number of contacts this broadcast gets sent to
                    this.$store.dispatch(
                        'broadcastsViewSelectedBroadcastTotalCount',
                        this.temp
                    )
                }
            })
        },
        popOptOutInfo(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'WARNING: Removing Opt-Out May Cause Delivery Failures',
                text: 'Messages without opt-out verbiage may get flagged or blocked by carriers. To edit your opt-out message, navigate to Settings -> Compliance settings',
                icon: 'mdi-alert',
                color: 'primary'
            })
        },
        popOrgNameInfo(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'WARNING: Removing Company Name May Cause Delivery Failures',
                text: 'Messages without company name may get flagged or blocked by carriers. To edit your company name, navigate to Settings -> Account Settings',
                icon: 'mdi-alert',
                color: 'primary'
            })
        },
        popSelectRecipient(): void {
            //  If there are any invalid groups, pop a dialog
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Group or contact is Required',
                text: 'Please pick a group or contact before continuing',
                icon: 'bi-exclamation-diamond-fill',
                color: 'primary'
            })
        }
    },
    watch: {
        // Watch temp data variable, done this was as it is a deep datastructure (object)
        temp: {
            deep: true,
            handler(): void {
                //  If message or media exist in broadcast
                if (this.temp.media || this.temp.message) {
                    //  Set messageEmpty to false, this is for conditional rendering
                    this.messageEmpty = false

                    const messageArea = document.getElementById('messageArea')

                    // Remove css class from messagetext form
                    if (messageArea) {
                        document
                            .getElementById('messageArea')
                            .classList.remove('shakeAnimation')
                    }
                }
            }
        }
    }
})
